// // src/slices/taskslice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { objectToQueryParams } from "../utils";

const base_url = `${process.env.REACT_APP_BASE_URL}`;

// Fetch all tasks
export const fetchAllTasks = createAsyncThunk(
  "task/fetchAllTasks",
  async ({ page, limit, filter }, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.userToken;
      const response = await axios.get(
        `${base_url}/api/tasks/?page=${page}&limit=${limit}&${objectToQueryParams(
          filter
        )}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return {
        tasks: response?.data?.data,
        total: response?.data?.total,
      };
    } catch (error) {
      if (error.response?.status === 404) {
        return { tasks: [], total: 0 };
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Fetch task by ID
export const fetchTasksById = createAsyncThunk(
  "task/fetchTasksById",
  async (taskId, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.userToken;
      const response = await axios.get(`${base_url}/api/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Create a new task
export const createTask = createAsyncThunk(
  "task/createTask",
  async (taskData, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.userToken;
      const response = await axios.post(`${base_url}/api/tasks`, taskData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Update a task
export const updateTask = createAsyncThunk(
  "task/updateTask",
  async (data, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.userToken;

      const response = await axios.put(
        `${base_url}/api/tasks/${data.id}`,
        data.taskData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  }
);

// Delete a task
export const deleteTask = createAsyncThunk(
  "task/deleteTask",
  async (id, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.userToken;
      await axios.delete(`${base_url}/api/tasks/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const taskSlice = createSlice({
  name: "task",
  initialState: {
    tasks: [],
    total: 0,
    loading: false,
    status: {
      fetchAllTasks: "idle",
      fetchTasksById: "idle",
      createTask: "idle",
      updateTask: "idle",
      deleteTask: "idle",
    },
    error: {
      fetchAllTasks: null,
      fetchTasksById: null,
      createTask: null,
      updateTask: null,
      deleteTask: null,
    },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch all tasks
      .addCase(fetchAllTasks.pending, (state) => {
        state.status.fetchAllTasks = "loading";
        state.loading = true;
      })
      .addCase(fetchAllTasks.fulfilled, (state, action) => {
        state.status.fetchAllTasks = "succeeded";
        state.loading = false;
        state.tasks = action.payload.tasks;
        state.total = action.payload.total;
      })
      .addCase(fetchAllTasks.rejected, (state, action) => {
        state.status.fetchAllTasks = "failed";
        state.loading = false;
        state.error.fetchAllTasks = action.payload;
      })

      // Fetch tasks by ID
      .addCase(fetchTasksById.pending, (state) => {
        state.status.fetchByUserId = "loading";
      })
      .addCase(fetchTasksById.fulfilled, (state, action) => {
        state.status.fetchByUserId = "succeeded";
        state.tasks = action.payload;
      })
      .addCase(fetchTasksById.rejected, (state, action) => {
        state.status.fetchByUserId = "failed";
        state.error.fetchByUserId = action.payload;
      })

      // Create a new task
      .addCase(createTask.pending, (state) => {
        state.status.createTask = "loading";
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.status.createTask = "succeeded";
        state.tasks.push(action.payload);
      })
      .addCase(createTask.rejected, (state, action) => {
        state.status.createTask = "failed";
        state.error.createTask = action.payload;
      })

      // Update a task
      .addCase(updateTask.pending, (state) => {
        state.status.updateTask = "loading";
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.status.updateTask = "succeeded";
        const index = state.tasks.findIndex(
          (task) => task._id === action.payload._id
        );
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.status.updateTask = "failed";
        state.error.updateTask = action.payload;
      })

      // Delete task
      .addCase(deleteTask.pending, (state) => {
        state.status.deleteTask = "loading";
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.status.deleteTask = "succeeded";
        state.tasks = state.tasks.filter((task) => task._id !== action.payload);
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.status.deleteTask = "failed";
        state.error.deleteTask = action.payload;
      });
  },
});

export default taskSlice.reducer;